import { type BillingOverviewDto } from '../../services/billing.api';
import { ApiError } from '../../services/core.api';
import { icon } from '../../icons';
import { type CreateDocumentRequest, type PropertyDto, type TenantDto, documentsApi } from '../../services/api';
import { t, translateDocumentType } from '../../services/i18n';
import { toastService } from '../../services/toast';
import { DOC_TYPES, esc, formatBytes, formatDate } from './document-shared';

type OpenDocumentUploadDialogOptions = {
  properties: PropertyDto[];
  tenants: TenantDto[];
  billingOverview: BillingOverviewDto | null;
  onUploaded: () => Promise<void>;
};

export function openDocumentUploadDialog(options: OpenDocumentUploadDialogOptions): void {
  const { properties, tenants, billingOverview, onUploaded } = options;
  const storageUsage = billingOverview?.storageUsage;

  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.innerHTML = `
    <div class="modal">
      <div class="modal-header">
        <h2>${t('document.upload')}</h2>
        <button class="modal-close" data-action="cancel">
          ${icon('x', { size: 20 })}
        </button>
      </div>
      <form>
        <div class="form-error" data-form-error hidden></div>
        <div class="profile-actions" data-upgrade-cta hidden>
          <a href="/pricing/" class="btn btn-outline" data-upgrade-link>${t('document.storageUpgradeButton')}</a>
        </div>
        <div class="form-group">
          <label>${t('common.file')} *</label>
          <input name="file" type="file" accept=".pdf,.jpg,.jpeg,.png" required>
        </div>
        <div class="form-group">
          <label>${t('common.type')} *</label>
          <select name="documentType" required>
            ${DOC_TYPES.map((type) => `<option value="${type}">${translateDocumentType(type)}</option>`).join('')}
          </select>
        </div>
        <div class="form-group">
          <label>${t('document.linkProperty')}</label>
          <select name="propertyId">
            <option value="">${t('common.noLink')}</option>
            ${properties.map((property) => `<option value="${property.id}">${esc(property.displayName || property.address)}</option>`).join('')}
          </select>
        </div>
        <div class="form-group">
          <label>${t('document.linkTenant')}</label>
          <select name="tenantId">
            <option value="">${t('common.noLink')}</option>
            ${tenants.map((tenant) => `<option value="${tenant.id}">${esc(tenant.firstName)} ${esc(tenant.lastName)} (${esc(tenant.propertyAddress)})</option>`).join('')}
          </select>
        </div>
        <div class="form-group">
          <label>${t('common.expiryDate')}</label>
          <input name="expiryDate" type="date">
        </div>
        <div class="form-group">
          <label>${t('common.tags')}</label>
          <input name="tags" placeholder="${t('document.tagsPlaceholder')}">
        </div>
        <div class="modal-actions">
          <button type="button" class="btn btn-outline" data-action="cancel">${t('common.cancel')}</button>
          <button type="submit" class="btn btn-primary">${t('common.upload')}</button>
        </div>
      </form>
    </div>
  `;

  const close = () => overlay.remove();
  overlay.addEventListener('click', (event) => { if (event.target === overlay) close(); });
  overlay.querySelectorAll('[data-action="cancel"]').forEach((button) => button.addEventListener('click', close));

  overlay.querySelector('form')?.addEventListener('submit', async (event) => {
    event.preventDefault();
    const submitBtn = overlay.querySelector('[type="submit"]') as HTMLButtonElement;
    const errorBox = overlay.querySelector<HTMLElement>('[data-form-error]');
    const upgradeCta = overlay.querySelector<HTMLElement>('[data-upgrade-cta]');
    const upgradeLink = overlay.querySelector<HTMLAnchorElement>('[data-upgrade-link]');
    submitBtn.disabled = true;
    submitBtn.textContent = t('common.uploading');
    if (errorBox) {
      errorBox.hidden = true;
      errorBox.textContent = '';
    }
    if (upgradeCta) {
      upgradeCta.hidden = true;
    }

    const formData = new FormData(event.currentTarget as HTMLFormElement);
    const file = formData.get('file') as File;
    if (!file) {
      submitBtn.disabled = false;
      submitBtn.textContent = t('common.upload');
      return;
    }

    if (storageUsage && file.size > storageUsage.remainingBytes) {
      if (errorBox) {
        errorBox.textContent = t('document.storageSelectionTooLarge', {
          fileSize: formatBytes(file.size),
          remaining: formatBytes(storageUsage.remainingBytes),
        });
        errorBox.hidden = false;
      }
      if (upgradeCta) {
        upgradeCta.hidden = false;
      }
      submitBtn.disabled = false;
      submitBtn.textContent = t('common.upload');
      return;
    }

    const propertyId = (formData.get('propertyId') as string) || undefined;
    const tenantId = (formData.get('tenantId') as string) || undefined;
    const request: CreateDocumentRequest = {
      propertyId,
      tenantId,
      fileName: file.name,
      contentType: file.type,
      fileSizeBytes: file.size,
      documentType: formData.get('documentType') as string,
      expiryDate: (formData.get('expiryDate') as string) || undefined,
      tags: (formData.get('tags') as string) || undefined,
    };

    try {
      const result = await documentsApi.create(request);
      const uploadResponse = await fetch(result.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file,
      });

      if (!uploadResponse.ok) {
        await documentsApi.delete(result.documentId).catch(() => undefined);
        throw new Error(t('document.uploadError'));
      }

      close();
      await onUploaded();
    } catch (error) {
      console.error('Failed to upload document:', error);
      if (error instanceof ApiError && error.code === 'DOCUMENT_STORAGE_LIMIT_EXCEEDED' && errorBox) {
        errorBox.textContent = error.message;
        errorBox.hidden = false;

        if (upgradeLink && upgradeCta) {
          const recommendedPlanKey = error.details?.recommendedPlanKey || '';
          upgradeLink.href = recommendedPlanKey
            ? `/pricing/?recommendedPlan=${encodeURIComponent(recommendedPlanKey)}`
            : '/pricing/';
          upgradeCta.hidden = false;
        }
      } else if (errorBox) {
        errorBox.textContent = (error as Error)?.message || t('document.uploadError');
        errorBox.hidden = false;
      }

      submitBtn.disabled = false;
      submitBtn.textContent = t('common.upload');
    }
  });

  document.body.appendChild(overlay);
}

export function openDocumentShareModal(url: string, expiresAt: string, onCopy: () => Promise<void>): void {
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.innerHTML = `
    <div class="modal">
      <div class="modal-header">
        <h2>${t('document.shareTitle')}</h2>
        <button class="modal-close" data-action="close">
          ${icon('x', { size: 20 })}
        </button>
      </div>
      <div class="form-group">
        <label>${t('document.shareLink')}</label>
        <input value="${esc(url)}" readonly>
      </div>
      <p class="text-muted">${t('document.shareExpires', { date: formatDate(expiresAt) })}</p>
      <div class="modal-actions">
        <button type="button" class="btn btn-outline" data-action="close">${t('common.close')}</button>
        <button type="button" class="btn btn-primary" data-action="copy">${t('common.copy')}</button>
      </div>
    </div>
  `;

  const close = () => overlay.remove();
  overlay.addEventListener('click', (event) => { if (event.target === overlay) close(); });
  overlay.querySelectorAll('[data-action="close"]').forEach((button) => button.addEventListener('click', close));
  overlay.querySelector('[data-action="copy"]')?.addEventListener('click', async () => {
    await onCopy();
    toastService.success(t('document.shareCopied'));
  });
  document.body.appendChild(overlay);
}
